/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import './vs-security-ddos-top-lists.less';

import * as l10n from './vs-security-ddos-top-lists.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

const componentName = 'vs-security-ddos-top-list';

/**
 * @ngdoc component
 * @name  virtualserviceSecurityDdosTable
 * @param {MetricsDimensionCollection} attacksCollection - Collection of attacks.
 * @param {MetricsDimensionCollection} ipgroupsCollection - Collection of client IP addresses.
 * @param {MetricsDimensionCollection} urlsCollection - Collection of URLs.
 * @param {MetricsDimensionCollection} asnsCollection - Collection of ASNs.
 * @param {MetricsDimensionCollection} blockedCollection - Collection of blocked client IPs.
 * @param {VirtualService} row - Virtual Service object containing runtime for Service Engine info.
 * @param {string} filter - Selected series to be filtered.
 * @param {function} onTabChange - Called when user selects a tab from the table.
 * @param {function} onSeriesSelect - Called when user selects a series to be filtered.
 * @description
 *     Displays DDoS collections in a table. The Attacks table is always shown, while the user can
 *     alternate between the Client IP, ASN, URL, and Blocked tables via tabs. Tables show Name,
 *     Rate, and Duration data. The Client IP and ASN tables display the AS Name associated with
 *     the IP address and AS number. The user can also select a filter to display series data and
 *     chart information. "View Full List" opens up a modal to display the collection grid for that
 *     selected tab by itself. Collections are passed in as bindings individually in order to use
 *     the $onChanges lifecycle hook to update gridConfig collections.
 */
class VsSecurityDdosTopListsController {
    constructor(NetworkSecurityPolicyDosCollection, AviModal, l10nService) {
        this.NetworkSecurityPolicyDosCollection_ = NetworkSecurityPolicyDosCollection;
        this.AviModal_ = AviModal;
        this.l10nKeys = l10nKeys;
        this.l10nService_ = l10nService;

        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Sets the active table tabs and gridConfigs.
     */
    $onInit() {
        this.active = 'attack';
        this.subactive = 'ipgroup';

        const { l10nService_: l10nService } = this;

        /**
         * Collection used to poll for blocked addresses.
         * @type {Collection}
         */
        this.securityPolicyCollection = new this.NetworkSecurityPolicyDosCollection_({
            params: {
                name: this.virtualservice.getName(),
            },
        });

        this.securityPolicyCollection.load();

        this.attackTypeGridConfig = {
            collection: this.attacksCollection,
            id: `${componentName}__attack-type`,
            fields: [{
                name: 'name',
                title: l10nService.getMessage(l10nKeys.columnTitleName),
                template:
                    `<vs-security-ddos-top-list-grid-cell-name
                        row="row"
                        on-series-select="config.options.handleSeriesSelect(filter)"
                        disable-select="config.options.disableSelect(filter)"
                        show-tooltip="true" />`,
            }, {
                name: 'duration',
                title: l10nService.getMessage(l10nKeys.columnTitleDuration),
                template: '<vs-security-ddos-top-list-grid-cell-duration row="row" />',
            }, {
                name: 'rate',
                title: l10nService.getMessage(l10nKeys.columnTitleRate),
                template:
                    '<vs-security-ddos-top-list-grid-cell-rate row="row"/>',
            }],
            rowClass: row => this.filterIsSelected(row.getName()) && 'selected' || '',
            layout: {
                hideDisplaying: true,
                hideSearch: true,
                hideEditColumns: true,
                hideTableNavigation: true,
            },
            options: {
                item: this.virtualservice,
                handleSeriesSelect: filter => this.onSeriesSelect({
                    filter: ['attack', filter],
                    dimension: this.active === 'blocked' ? 'ipgroup' : this.active,
                }),
                disableSelect: filter => this.disableSelect('attack', filter),
            },
        };

        this.clientipGridConfig = {
            collection: this.ipgroupsCollection,
            id: `${componentName}__client-ip`,
            fields: [{
                name: 'ip',
                title: l10nService.getMessage(l10nKeys.columnTitleIpAddress),
                template:
                    `<vs-security-ddos-top-list-grid-cell-name
                        row="row"
                        on-series-select="config.options.handleSeriesSelect(filter)"
                        disable-select="config.options.disableSelect(filter)" />`,
            }, {
                name: 'asname',
                title: l10nService.getMessage(l10nKeys.columnTitleAsName),
                template: '{{ row.data.asname }}',
            }, {
                name: 'duration',
                title: l10nService.getMessage(l10nKeys.columnTitleDuration),
                template: '<vs-security-ddos-top-list-grid-cell-duration row="row" />',
            }, {
                name: 'rate',
                title: l10nService.getMessage(l10nKeys.columnTitleRate),
                template:
                    `<vs-security-ddos-top-list-grid-cell-rate
                        row="row"
                        metric-data="config.options.item.data" />`,
            }],
            rowClass:
                row => this.filterIsSelected(row.getName()) && 'selected' || '',
            singleactions: [{
                title: l10nService.getMessage(l10nKeys.actionTitleBlock),
                class: 'icon-block',
                do: row => {
                    row.blockAddress(this.virtualservice.data.config)
                        .then(() => {
                            this.securityPolicyCollection.load();
                            this.blockedCollection.load();
                        });
                },
                hidden: row => this.securityPolicyCollection.blockedAddressesHash[row.id],
            }],
            layout: {
                hideDisplaying: true,
                hideSearch: true,
                hideEditColumns: true,
                hideTableNavigation: true,
            },
            options: {
                item: this.virtualservice,
                handleSeriesSelect: filter => this.onSeriesSelect({
                    filter: ['ipgroup', filter],
                    dimension: 'attack',
                }),
                disableSelect: filter => this.disableSelect('ipgroup', filter),
            },
            permission: 'PERMISSION_NETWORKSECURITYPOLICY',
        };

        this.urlGridConfig = {
            collection: this.urlsCollection,
            id: `${componentName}__url`,
            fields: [{
                name: 'name',
                title: 'URL',
                template:
                    `<vs-security-ddos-top-list-grid-cell-name
                        row="row"
                        on-series-select="config.options.handleSeriesSelect(filter)"
                        disable-select="config.options.disableSelect(filter)" />`,
            }, {
                name: 'duration',
                title: l10nService.getMessage(l10nKeys.columnTitleDuration),
                template: '<vs-security-ddos-top-list-grid-cell-duration row="row" />',
            }, {
                name: 'rate',
                title: l10nService.getMessage(l10nKeys.columnTitleRate),
                template:
                    `<vs-security-ddos-top-list-grid-cell-rate
                        row="row"
                        metric-data="config.options.item.data" />`,
            }],
            rowClass: row => this.filterIsSelected(row.getName()) && 'selected' || '',
            layout: {
                hideDisplaying: true,
                hideSearch: true,
                hideEditColumns: true,
                hideTableNavigation: true,
            },
            options: {
                item: this.virtualservice,
                handleSeriesSelect: filter => this.onSeriesSelect({
                    filter: ['url', filter],
                    dimension: 'attack',
                }),
                disableSelect: filter => this.disableSelect('url', filter),
            },
        };

        this.asnGridConfig = {
            collection: this.asnsCollection,
            id: `${componentName}__asn`,
            fields: [{
                name: 'asn',
                title: l10nService.getMessage(l10nKeys.columnTitleAsNumber),
                template:
                    `<vs-security-ddos-top-list-grid-cell-name
                        row="row"
                        on-series-select="config.options.handleSeriesSelect(filter, dimension)"
                        disable-select="config.options.disableSelect(filter)" />`,
            }, {
                name: 'asname',
                title: l10nService.getMessage(l10nKeys.columnTitleAsName),
                template: '{{ row.data.asname }}',
            }, {
                name: 'duration',
                title: l10nService.getMessage(l10nKeys.columnTitleDuration),
                template: '<vs-security-ddos-top-list-grid-cell-duration row="row" />',
            }, {
                name: 'rate',
                title: l10nService.getMessage(l10nKeys.columnTitleRate),
                template:
                    `<vs-security-ddos-top-list-grid-cell-rate
                        row="row"
                        metric-data="config.options.item.data" />`,
            }],
            rowClass: row => this.filterIsSelected(row.getName()) && 'selected' || '',
            layout: {
                hideDisplaying: true,
                hideSearch: true,
                hideEditColumns: true,
                hideTableNavigation: true,
            },
            options: {
                item: this.virtualservice,
                handleSeriesSelect: filter => this.onSeriesSelect({
                    filter: ['asn', filter],
                    dimension: 'attack',
                }),
                disableSelect: filter => this.disableSelect('asn', filter),
            },
        };

        this.blockedGridConfig = {
            collection: this.blockedCollection,
            id: `${componentName}__blocked`,
            fields: [{
                name: 'ip',
                title: l10nService.getMessage(l10nKeys.columnTitleIpAddress),
                template:
                    `<vs-security-ddos-top-list-grid-cell-name
                        row="row"
                        on-series-select="config.options.handleSeriesSelect(filter)"
                        disable-select="config.options.disableSelect(filter)" />`,
            }, {
                name: 'asname',
                title: l10nService.getMessage(l10nKeys.columnTitleAsName),
                template: '{{ row.data.asname }}',
            }, {
                name: 'duration',
                title: l10nService.getMessage(l10nKeys.columnTitleDuration),
                template: '<vs-security-ddos-top-list-grid-cell-duration row="row" />',
            }, {
                name: 'rate',
                title: l10nService.getMessage(l10nKeys.columnTitleRate),
                template:
                    `<vs-security-ddos-top-list-grid-cell-rate
                        row="row"
                        metric-data="config.options.item.data" />`,
            }],
            singleactions: [{
                title: l10nService.getMessage(l10nKeys.actionTitleUnblock),
                class: 'icon-check',
                do: row => {
                    row.unblockAddress(this.virtualservice.data.config)
                        .then(() => {
                            this.securityPolicyCollection.load();
                            this.blockedCollection.load();
                        });
                },
                hidden: () => false,
            }],
            layout: {
                hideDisplaying: true,
                hideSearch: true,
                hideEditColumns: true,
                hideTableNavigation: true,
            },
            options: {
                item: this.virtualservice,
                handleSeriesSelect: filter => this.onSeriesSelect({
                    filter: ['ipgroup', filter],
                    dimension: 'attack',
                }),
                disableSelect: filter => this.disableSelect('blocked', filter),
            },
            permission: 'PERMISSION_NETWORKSECURITYPOLICY',
        };
    }

    /**
     * Checks for changes to the bound collections to reset the gridConfig collections.
     * @param  {Object} changes - angular onChanges object.
     */
    $onChanges(changes) {
        if (_.sample(changes).isFirstChange()) {
            return;
        }

        if (changes.attacksCollection) {
            this.attackTypeGridConfig.collection = changes.attacksCollection.currentValue;
        }

        if (changes.ipgroupsCollection) {
            this.clientipGridConfig.collection = changes.ipgroupsCollection.currentValue;
        }

        if (changes.urlsCollection) {
            this.urlGridConfig.collection = changes.urlsCollection.currentValue;
        }

        if (changes.asnsCollection) {
            this.asnGridConfig.collection = changes.asnsCollection.currentValue;
        }
    }

    /**
     * Sets the active property to the selected tab.
     * @param  {string} tab - Name of the tab to be set to active.
     */
    selectTab(tab) {
        this.active = tab;

        if (tab !== 'attack') {
            this.subactive = tab;
        }

        this.onTabChange({ tab });
    }

    filterIsSelected(filter) {
        return filter === this.filter;
    }

    /**
     * Returns true to disable filters from being clickable if the filter's tab is the same as
     * the active tab, or if the active tab is 'blocked'.
     * @param  {string} tab - Name of the tab.
     * @param {string} filter - Name of the filter.
     * @return {boolean}
     */
    disableSelect(tab, filter) {
        return this.active === tab || this.active === 'blocked' || this.filter === filter;
    }

    /**
     * Opens the modal to show the full list of the DDoS collection.
     */
    viewFullList() {
        const { l10nService_: l10nService } = this;

        const bindingHash = {
            attack: [l10nService.getMessage(l10nKeys.attackTypesTitle), this.attackTypeGridConfig],
            ipgroup: [
                l10nService.getMessage(l10nKeys.attackingClientIpsTitle),
                this.clientipGridConfig,
            ],
            url: [l10nService.getMessage(l10nKeys.attackingUrlsTitle), this.urlGridConfig],
            asn: [l10nService.getMessage(l10nKeys.attackingAsnsTitle), this.asnGridConfig],
            blocked: [
                l10nService.getMessage(l10nKeys.activeClientsBlockedTitle),
                this.blockedGridConfig,
            ],
        };

        const modalTuple = bindingHash[this.active];

        this.AviModal_.open('virtualservice-security-ddos-full-list', {
            title: modalTuple[0],
            gridConfig: modalTuple[1],
        });
    }

    $onDestroy() {
        this.securityPolicyCollection.destroy();
    }
}

VsSecurityDdosTopListsController.$inject = [
    'NetworkSecurityPolicyDosCollection',
    'AviModal',
    'l10nService',
];

angular.module('avi/vs/security').component('vsSecurityDdosTopLists', {
    bindings: {
        attacksCollection: '<',
        ipgroupsCollection: '<',
        urlsCollection: '<',
        asnsCollection: '<',
        blockedCollection: '<',
        virtualservice: '<',
        filter: '@',
        onTabChange: '&',
        onSeriesSelect: '&',
    },
    controller: VsSecurityDdosTopListsController,
    templateUrl: 'src/components/applications/virtualservice/vs-security-tab/' +
            'vs-security-ddos/vs-security-ddos-top-lists/vs-security-ddos-top-lists.html',
});
